<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DocumentForm from './DocumentForm.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'createSuccess']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const formRef = ref(null);
const discardDialog = ref(null);
const saving = ref(false);
const saveError = ref('');
const { currentAccount } = useAccount();
let active = true;
onBeforeUnmount(() => {
  active = false;
});

const i18nKey = 'CAPTAIN.DOCUMENTS.CREATE';

const handleSubmit = async newDocument => {
  if (saving.value) return;
  const accountId = currentAccount.value?.id;
  const assistantId = props.assistantId;
  const current = () =>
    active &&
    accountId === currentAccount.value?.id &&
    assistantId === props.assistantId;
  saving.value = true;
  saveError.value = '';
  try {
    await store.dispatch('captainDocuments/create', newDocument);
    if (!current()) return;
    emit('createSuccess');
    useAlert(t(`${i18nKey}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    if (!current()) return;
    const parsedError = parseAPIErrorResponse(error);
    saveError.value =
      (typeof parsedError === 'string' ? parsedError : parsedError?.message) ||
      t(`${i18nKey}.ERROR_MESSAGE`);
  } finally {
    saving.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  if (saving.value) return;
  if (formRef.value?.isDirty) {
    discardDialog.value.open();
    return;
  }
  dialogRef.value.close();
};
const discardChanges = () => {
  discardDialog.value.close();
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    confirm-before-close
    :title="$t(`${i18nKey}.TITLE`)"
    :description="$t('CAPTAIN.DOCUMENTS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @request-close="handleCancel"
    @confirm="formRef?.submit()"
    @close="handleClose"
  >
    <p
      v-if="saveError"
      role="alert"
      class="mb-4 rounded-xl border border-n-weak p-3 text-sm text-n-ruby-11"
    >
      {{ saveError }}
    </p>
    <DocumentForm
      ref="formRef"
      root-tag="div"
      :saving="saving"
      :assistant-id="assistantId"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
  <Dialog
    ref="discardDialog"
    type="alert"
    :title="t('CAPTAIN.DOCUMENTS.DRAFT.TITLE')"
    :description="t('CAPTAIN.DOCUMENTS.DRAFT.DESCRIPTION')"
    :confirm-button-label="t('CAPTAIN.DOCUMENTS.DRAFT.DISCARD')"
    :cancel-button-label="t('CAPTAIN.DOCUMENTS.DRAFT.KEEP')"
    @confirm="discardChanges"
  />
</template>
